import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { serverApiFetch } from '../../../services/server-api-client'
import { errorMessage } from '@/lib/utils'

// Types
export interface Creative {
  id: string
  name: string
  /** Server platform string (instagram, facebook, linkedin, pinterest, …). */
  type: string
  data: Record<string, unknown> | null
  creatorId: string
  businessId: string | null
  businessName: string | null
  /** Publish/schedule state — additive list fields (older servers omit them). */
  scheduledAt?: string | null
  publishedAt?: string | null
  generationStatus?: 'pending' | 'generating' | 'ready' | 'failed' | null
  createdAt: string
  updatedAt: string
}

export interface CreativeDetail extends Creative {
  business?: { id: string; name: string } | null
  chat?: {
    id: string
    status: string
    title: string | null
    createdAt: string
  } | null
  generationError?: string | null
}

/**
 * One persisted chat message part. Several historical wire formats coexist
 * (AI SDK persisted, live useChat, legacy Anthropic tool_use), so every field
 * is optional and renderers narrow at runtime.
 */
export interface MessagePartObject {
  type?: string
  text?: string
  content?: string
  toolName?: string
  name?: string
  toolInvocation?: { toolName?: string }
  [key: string]: unknown
}

export type MessagePart = string | MessagePartObject

export interface CreativeChatMessage {
  id: string
  chatId: string
  role: 'user' | 'assistant' | 'system'
  parts: MessagePart[]
  order: number
  createdAt: string
}

interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// NOTE: the roster list + stats moved to react-query (`hooks/use-creatives.ts`,
// used by pages/Creatives.tsx). This slice keeps only the detail/chat/user
// pieces still consumed by UserDetailView and BusinessDetailView.
interface CreativesState {
  currentCreative: CreativeDetail | null
  currentCreativeLoading: boolean

  chatMessages: CreativeChatMessage[]
  chatMessagesLoading: boolean

  // User's creatives (for UserDetailView)
  userCreatives: Record<
    string,
    {
      data: Creative[]
      pagination: PaginationMeta | null
      loading: boolean
      error: string | null
      fetched: boolean
    }
  >
}

const initialState: CreativesState = {
  currentCreative: null,
  currentCreativeLoading: false,

  chatMessages: [],
  chatMessagesLoading: false,

  userCreatives: {},
}

// Thunks
export const fetchCreativeById = createAsyncThunk(
  'creatives/fetchById',
  async (creativeId: string, { rejectWithValue }) => {
    try {
      return await serverApiFetch<CreativeDetail>(`/v1/admin/creatives/${creativeId}`)
    } catch (error) {
      return rejectWithValue(errorMessage(error) || 'Failed to fetch creative')
    }
  }
)

export const fetchCreativeChatMessages = createAsyncThunk(
  'creatives/fetchChatMessages',
  async (params: { creativeId: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams()
      queryParams.set('page', (params.page || 1).toString())
      queryParams.set('limit', (params.limit || 50).toString())

      return await serverApiFetch<{
        chat: { id: string; status: string; title: string | null; createdAt: string } | null
        data: CreativeChatMessage[]
        pagination: PaginationMeta
      }>(`/v1/admin/creatives/${params.creativeId}/chat?${queryParams.toString()}`)
    } catch (error) {
      return rejectWithValue(errorMessage(error) || 'Failed to fetch chat messages')
    }
  }
)

export const fetchUserCreatives = createAsyncThunk(
  'creatives/fetchUserCreatives',
  async (
    params: { userId: string; page?: number; limit?: number; type?: string },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams()
      queryParams.set('page', (params.page || 1).toString())
      queryParams.set('limit', (params.limit || 20).toString())
      if (params.type) queryParams.set('type', params.type)

      const response = await serverApiFetch<{ data: Creative[]; pagination: PaginationMeta }>(
        `/v1/admin/creatives/user/${params.userId}?${queryParams.toString()}`
      )
      return { userId: params.userId, ...response }
    } catch (error) {
      return rejectWithValue({
        message: errorMessage(error) || 'Failed to fetch user creatives',
        userId: params.userId,
      })
    }
  }
)

// Slice
const creativesSlice = createSlice({
  name: 'creatives',
  initialState,
  reducers: {
    clearCurrentCreative: state => {
      state.currentCreative = null
      state.chatMessages = []
    },
    clearUserCreatives: (state, action: PayloadAction<string>) => {
      delete state.userCreatives[action.payload]
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCreativeById.pending, state => {
        state.currentCreativeLoading = true
      })
      .addCase(fetchCreativeById.fulfilled, (state, action) => {
        state.currentCreative = action.payload
        state.currentCreativeLoading = false
      })
      .addCase(fetchCreativeById.rejected, state => {
        state.currentCreativeLoading = false
      })

    builder
      .addCase(fetchCreativeChatMessages.pending, state => {
        state.chatMessagesLoading = true
      })
      .addCase(fetchCreativeChatMessages.fulfilled, (state, action) => {
        state.chatMessages = action.payload.data
        state.chatMessagesLoading = false
      })
      .addCase(fetchCreativeChatMessages.rejected, state => {
        state.chatMessagesLoading = false
      })

    builder
      .addCase(fetchUserCreatives.pending, (state, action) => {
        const userId = action.meta.arg.userId
        state.userCreatives[userId] = {
          data: state.userCreatives[userId]?.data || [],
          pagination: state.userCreatives[userId]?.pagination || null,
          loading: true,
          error: null,
          fetched: state.userCreatives[userId]?.fetched || false,
        }
      })
      .addCase(fetchUserCreatives.fulfilled, (state, action) => {
        const { userId, data, pagination } = action.payload
        state.userCreatives[userId] = {
          data,
          pagination,
          loading: false,
          error: null,
          fetched: true,
        }
      })
      .addCase(fetchUserCreatives.rejected, (state, action) => {
        const { userId, message } = action.payload as { userId: string; message: string }
        if (state.userCreatives[userId]) {
          state.userCreatives[userId].loading = false
          state.userCreatives[userId].error = message
          state.userCreatives[userId].fetched = true
        }
      })
  },
})

export const { clearCurrentCreative, clearUserCreatives } = creativesSlice.actions
export const creativesReducer = creativesSlice.reducer
export default creativesSlice.reducer

// Selectors
const selectCreativesState = (state: { creatives: CreativesState }) => state.creatives

export const selectCurrentCreative = createSelector(
  [selectCreativesState],
  state => state.currentCreative
)

export const selectCurrentCreativeLoading = createSelector(
  [selectCreativesState],
  state => state.currentCreativeLoading
)

export const selectCreativeChatMessages = createSelector(
  [selectCreativesState],
  state => state.chatMessages
)

export const selectCreativeChatMessagesLoading = createSelector(
  [selectCreativesState],
  state => state.chatMessagesLoading
)

export const selectUserCreatives = createSelector(
  [selectCreativesState, (_state: { creatives: CreativesState }, userId: string) => userId],
  (state, userId) => state.userCreatives[userId]?.data || []
)

export const selectUserCreativesLoading = createSelector(
  [selectCreativesState, (_state: { creatives: CreativesState }, userId: string) => userId],
  (state, userId) => state.userCreatives[userId]?.loading || false
)

export const selectUserCreativesPagination = createSelector(
  [selectCreativesState, (_state: { creatives: CreativesState }, userId: string) => userId],
  (state, userId) => state.userCreatives[userId]?.pagination || null
)

export const selectUserCreativesFetched = createSelector(
  [selectCreativesState, (_state: { creatives: CreativesState }, userId: string) => userId],
  (state, userId) => state.userCreatives[userId]?.fetched || false
)
